import type {
  PortalCoverage,
  PortalSeriesPoint,
  PortalYearOnYear,
} from "../../types/portalReport.js";
import { enumerateMonths, monthKeyOf, monthLabel } from "./series.js";

// CLIENT PORTAL RESTYLE — twelve rolling months, this year against last.
//
// ⚠️⚠️ WHY THIS IS NOT `thirteenMonthSeries` WITH A LONGER RANGE. That series is
// ONE continuous run of months; a year-on-year chart needs TWO runs, aligned by
// calendar month, and there is no way to read one off the other. So the daily
// fetch is widened to 24 months and split here — one query still serves every
// chart on the page.
//
// ⚠️⚠️ THE PRIOR YEAR IS EITHER COMPLETE OR ABSENT, and that is the whole
// design. Reporting backfill is tiered: plenty of accounts hold only the recent
// 90 days. Drawing 2025 with eight empty months beside a full 2026 reads as a
// business that collapsed and recovered — the exact misreading the report's
// no-data-is-not-zero rule exists to prevent, just at series scale rather than
// bucket scale. So a prior year that is not measured throughout is withheld, and
// the page SAYS WHY in words.
//
// ⚠️ `previous[i]` is the same CALENDAR month as `current[i]`, one year earlier —
// NOT the same number of days into the month. A partial current month against a
// complete one a year ago is a real distortion, so the page states it rather
// than quietly comparing thirteen days with thirty-one.

/** How many months the chart shows, and therefore how far back the fetch goes. */
export const YEAR_ON_YEAR_MONTHS = 12;

/** The month key twelve months before `monthKey` ('2026-08' → '2025-08'). */
export function priorYearMonth(monthKey: string): string {
  const y = Number(monthKey.slice(0, 4));
  const m = monthKey.slice(5, 7);
  return `${y - 1}-${m}`;
}

/**
 * The FULL range the daily fetch must cover to serve this chart: the first day
 * of the earliest prior-year month through `today`.
 *
 * ⚠️ 24 months, not 12. Both runs come out of one query, so the alternative
 * would be a second round trip per channel for data the first one could already
 * have carried.
 */
export function yearOnYearRange(today: string): { from: string; to: string } {
  const months = yearOnYearMonths(today);
  const first = months[0] as string;
  return { from: `${priorYearMonth(first)}-01`, to: today };
}

/** The twelve month keys ending in the month of `today`, oldest first. */
export function yearOnYearMonths(today: string): string[] {
  const endY = Number(today.slice(0, 4));
  const endM = Number(today.slice(5, 7));
  const total = endY * 12 + (endM - 1) - (YEAR_ON_YEAR_MONTHS - 1);
  const fromY = Math.floor(total / 12);
  const fromM = (total % 12) + 1;
  const from = `${fromY}-${String(fromM).padStart(2, "0")}-01`;
  return enumerateMonths(from, monthKeyOf(today));
}

/**
 * Why the prior year cannot honestly be drawn, or null when it can.
 *
 * ⚠️ ANY unmeasured prior-year month withholds the WHOLE series. One reading of
 * "a half-empty 2025 series" would be "draw it with gaps"; the gaps machinery
 * can do that, and it is still the wrong answer here — a reader comparing two
 * lines reads the SHAPE, and a line that vanishes for a third of its length
 * distorts the shape of the part that remains. Withhold and explain.
 */
export function priorYearUnavailableReason(
  previous: readonly PortalSeriesPoint[],
  coverage: PortalCoverage,
): string | null {
  if (previous.length === 0)
    return "There is no reporting history for the year before this one.";
  const missing = previous.filter((p) => p.coverage === "no-data");
  if (missing.length === 0) return null;
  if (!coverage.firstDate) {
    return "Last year is not shown: there is no reporting history for this account yet.";
  }
  const from = monthLabel(monthKeyOf(coverage.firstDate));
  // ⚠️ THE FACT, NOT THE JUSTIFICATION. It used to add "an incomplete comparison
  // line would read as a fall rather than as an absence of data" — an argument
  // written for us, about a decision the client did not make. The date is the
  // part they can act on. The RULE it explains is unchanged and still enforced
  // above: a prior year that is not measured throughout is withheld entirely.
  return `Last year is not shown: reporting for this account starts in ${from}.`;
}

/**
 * Split a 24-month monthly series into the two aligned twelve-month runs.
 *
 * ⚠️ The input MUST be exactly the months `yearOnYearRange` asked for, built by
 * the same `buildSeries` every other chart uses — so the coverage states, the
 * null handling and the CPL re-derivation are the shared ones rather than a
 * second implementation living here.
 */
export function splitYearOnYear(
  months: readonly PortalSeriesPoint[],
  today: string,
  coverage: PortalCoverage,
): PortalYearOnYear {
  const wanted = yearOnYearMonths(today);
  const byBucket = new Map(months.map((p) => [p.bucket, p]));
  const current = wanted.map((key) => byBucket.get(key) ?? missingPoint(key));
  const previous = wanted.map((key) => {
    const prior = priorYearMonth(key);
    return byBucket.get(prior) ?? missingPoint(prior);
  });
  const reason = priorYearUnavailableReason(previous, coverage);
  const currentYear = Number((wanted.at(-1) as string).slice(0, 4));
  return {
    current,
    // ⚠️ WITHHELD, not gapped. See the header.
    previous: reason ? [] : previous,
    previousUnavailableReason: reason,
    currentLabel: String(currentYear),
    previousLabel: String(currentYear - 1),
  };
}

/**
 * A month the series did not carry at all.
 *
 * ⚠️ `no-data`, never a zero. Reaching this means the fetch did not cover the
 * month, which is precisely "we do not know" — and the whole point of the type
 * is that we never dress that up as a measurement.
 */
function missingPoint(bucket: string): PortalSeriesPoint {
  return {
    bucket,
    label: monthLabel(bucket),
    coverage: "no-data",
    leads: null,
    spend: null,
    costPerLead: null,
    // ⚠️ The delivery counts are null here for the same reason as the rest: this
    // month was not covered by the fetch at all, which is "we do not know".
    impressions: null,
    clicks: null,
  };
}
